import { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Upload, Trash2, Copy, Pencil, KeyRound } from "lucide-react";

const ALL_MODULES = ["dashboard", "customers", "sites", "contracts", "assets", "jobs", "schedule", "defects", "quotations", "stock", "purchasing", "projects", "people", "vehicles", "billing", "documents", "reports", "settings"];

export default function Settings() {
  const { company, reload } = useTenant();
  const { user } = useAuth();
  const [form, setForm] = useState({});
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [newUser, setNewUser] = useState({});
  const [uOpen, setUOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [resetPw, setResetPw] = useState("");
  const [delUser, setDelUser] = useState(null);
  const canEdit = ["amden_super_admin", "ops_owner"].includes(user?.role);
  const isSuper = user?.role === "amden_super_admin";
  const reloadUsers = () => api.get("/users").then((r) => setUsers(r.data));

  useEffect(() => { if (company) setForm({ ...company }); }, [company]);
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data)); api.get("/meta").then((r) => setRoles(r.data.roles)); }, []);

  const save = async () => {
    const { nav, modules, ...body } = form;
    if (isSuper) body.modules = modules;
    await api.put("/company/config", body); toast.success("Company settings saved"); reload();
  };
  const toggleModule = (m) => {
    const mods = form.modules || []; const has = mods.includes(m);
    setForm((f) => ({ ...f, modules: has ? mods.filter((x) => x !== m) : [...mods, m] }));
  };
  const createUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.role) return toast.error("Name, email and role required");
    try { await api.post("/users", newUser); toast.success("User created"); setUOpen(false); setNewUser({}); api.get("/users").then((r) => setUsers(r.data)); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const saveUserEdit = async () => {
    const { id, name, email, role, department, phone, status } = editUser;
    try { await api.put(`/users/${id}`, { name, email, role, department, phone, status }); toast.success("User updated"); setEditUser(null); reloadUsers(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const doResetPassword = async () => {
    try { await api.post(`/users/${resetUser.id}/reset-password`, { password: resetPw }); toast.success(`Password reset for ${resetUser.name}`); setResetUser(null); setResetPw(""); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const doDeleteUser = async () => {
    try { await api.delete(`/users/${delUser.id}`); toast.success("User deleted"); setDelUser(null); reloadUsers(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const logoRef = useRef(null);
  const uploadLogo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/companies/${company.id}/logo`, fd); toast.success("Logo uploaded"); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    e.target.value = "";
  };
  const removeLogo = async () => {
    try { await api.delete(`/companies/${company.id}/logo`); toast.success("Logo removed"); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const loginUrl = company?.slug ? `${window.location.origin}/login/${company.slug}` : "";
  const copyLink = () => { navigator.clipboard.writeText(loginUrl); toast.success("Sign-in link copied"); };

  if (!company) return null;

  return (
    <div>
      <h1 className="text-2xl font-head font-bold text-slate-900 mb-4">Settings</h1>
      <Tabs defaultValue="company">
        <TabsList>
          <TabsTrigger value="company" data-testid="settings-tab-company">Company & Branding</TabsTrigger>
          {isSuper && <TabsTrigger value="modules" data-testid="settings-tab-modules">Modules</TabsTrigger>}
          <TabsTrigger value="users" data-testid="settings-tab-users">Users & Roles</TabsTrigger>
        </TabsList>

        <TabsContent value="company" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-md p-5 max-w-2xl grid grid-cols-1 md:grid-cols-2 gap-4">
            {[["name", "Company name"], ["trading_name", "Trading name"], ["address", "Address"], ["contact_email", "Contact email"], ["contact_phone", "Contact phone"], ["currency", "Currency"], ["labour_cost_rate", "Labour cost rate (per hour)"], ["date_format", "Date format"], ["timezone", "Time zone"]].map(([k, l]) => (
              <div key={k}><Label className="text-xs text-slate-600">{l}</Label><Input data-testid={`settings-${k}`} disabled={!canEdit} value={form[k] || ""} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} className="mt-1 bg-white" /></div>
            ))}
            <div><Label className="text-xs text-slate-600">Brand accent colour</Label>
              <div className="flex items-center gap-2 mt-1">
                <input type="color" data-testid="settings-accent" disabled={!canEdit} value={form.accent_color || "#FFA502"} onChange={(e) => setForm((f) => ({ ...f, accent_color: e.target.value }))} className="h-9 w-14 rounded border border-slate-200" />
                <Input value={form.accent_color || ""} onChange={(e) => setForm((f) => ({ ...f, accent_color: e.target.value }))} className="bg-white" />
              </div>
            </div>
            <div><Label className="text-xs text-slate-600">Terminology for "Job"</Label><Input data-testid="settings-terminology" disabled={!canEdit} value={form.terminology?.job || ""} onChange={(e) => setForm((f) => ({ ...f, terminology: { ...(f.terminology || {}), job: e.target.value, jobs: e.target.value + "s" } }))} className="mt-1 bg-white" /></div>
            <div className="md:col-span-2"><Label className="text-xs text-slate-600">Company logo — shown on your branded sign-in page</Label>
              <div className="flex items-center gap-3 mt-1.5">
                {company.logo ? (
                  <img data-testid="settings-logo-preview" src={`${API}/public/companies/${company.id}/logo?v=${company.logo_version || 0}`} alt="Company logo" className="h-12 max-w-[160px] object-contain border border-slate-200 rounded-md bg-white p-1" />
                ) : (
                  <div className="h-12 w-12 rounded-md grid place-items-center text-white font-head font-bold text-lg" style={{ backgroundColor: form.accent_color || "#FFA502" }}>{(company.name || "?")[0]}</div>
                )}
                {canEdit && (<>
                  <input ref={logoRef} type="file" accept=".png,.jpg,.jpeg,.webp,.svg" className="hidden" onChange={uploadLogo} />
                  <Button type="button" variant="outline" data-testid="settings-logo-upload" onClick={() => logoRef.current?.click()}><Upload className="h-4 w-4 mr-1" />Upload logo</Button>
                  {company.logo && <Button type="button" variant="outline" data-testid="settings-logo-remove" onClick={removeLogo} className="text-red-600 hover:text-red-700"><Trash2 className="h-4 w-4 mr-1" />Remove</Button>}
                </>)}
              </div>
              <p className="text-[11px] text-slate-400 mt-1">PNG, JPG, WEBP or SVG · max 2 MB</p>
            </div>
            {loginUrl && (
              <div className="md:col-span-2"><Label className="text-xs text-slate-600">Branded sign-in link</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input readOnly data-testid="settings-login-link" value={loginUrl} className="bg-slate-50 text-slate-600" />
                  <Button type="button" variant="outline" data-testid="settings-login-copy" onClick={copyLink}><Copy className="h-4 w-4" /></Button>
                </div>
                <p className="text-[11px] text-slate-400 mt-1">Your team sees your logo and colours when signing in at this address.</p>
              </div>
            )}
            {canEdit && <div className="md:col-span-2"><Button className="btn-accent" data-testid="settings-save" onClick={save}>Save settings</Button></div>}
          </div>
        </TabsContent>

        {isSuper && (
        <TabsContent value="modules" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-md p-5 max-w-2xl">
            <p className="text-sm text-slate-500 mb-3">Enable or disable modules for {company.name}. Only enabled modules appear in navigation. Module control is reserved for Amden Super Admin.</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {ALL_MODULES.map((m) => (
                <label key={m} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                  <span className="text-sm capitalize text-slate-700">{m}</span>
                  <Switch data-testid={`module-toggle-${m}`} disabled={m === "dashboard" || m === "settings"} checked={(form.modules || []).includes(m)} onCheckedChange={() => toggleModule(m)} />
                </label>
              ))}
            </div>
            <Button className="btn-accent mt-4" data-testid="modules-save" onClick={save}>Save modules</Button>
          </div>
        </TabsContent>
        )}

        <TabsContent value="users" className="mt-4">
          <div className="flex justify-end mb-3">{canEdit && <Button className="btn-accent" data-testid="user-new-btn" onClick={() => setUOpen(true)}><Plus className="h-4 w-4 mr-1" />Add user</Button>}</div>
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50"><tr>{["Name", "Email", "Role", "Department", "Status"].map((h) => <th key={h} className="text-left font-medium text-slate-500 px-3 py-2.5">{h}</th>)}{isSuper && <th className="w-28"></th>}</tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t border-slate-100"><td className="px-3 py-2.5 font-medium text-slate-800">{u.name}</td>
                    <td className="px-3 py-2.5 text-slate-600">{u.email}</td>
                    <td className="px-3 py-2.5 text-slate-600">{roles.find((r) => r.value === u.role)?.label || u.role}</td>
                    <td className="px-3 py-2.5 text-slate-500">{u.department || "—"}</td>
                    <td className="px-3 py-2.5 text-slate-500">{u.status || "active"}</td>
                    {isSuper && (
                      <td className="px-2 py-2.5">
                        {u.role !== "amden_super_admin" && (
                          <div className="flex items-center gap-1 justify-end">
                            <button data-testid={`user-edit-${u.id}`} title="Edit user" onClick={() => setEditUser({ ...u })} className="text-slate-400 hover:text-slate-700 transition-colors p-1"><Pencil className="h-4 w-4" /></button>
                            <button data-testid={`user-reset-${u.id}`} title="Reset password" onClick={() => { setResetUser(u); setResetPw(""); }} className="text-slate-400 hover:text-amber-600 transition-colors p-1"><KeyRound className="h-4 w-4" /></button>
                            <button data-testid={`user-delete-${u.id}`} title="Delete user" onClick={() => setDelUser(u)} className="text-slate-400 hover:text-red-600 transition-colors p-1"><Trash2 className="h-4 w-4" /></button>
                          </div>
                        )}
                      </td>
                    )}</tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={uOpen} onOpenChange={setUOpen}>
        <DialogContent onInteractOutside={(e) => e.preventDefault()} className="bg-white">
          <DialogHeader><DialogTitle className="font-head">Add user</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-slate-600">Name</Label><Input data-testid="new-user-name" value={newUser.name || ""} onChange={(e) => setNewUser((u) => ({ ...u, name: e.target.value }))} className="mt-1" /></div>
            <div><Label className="text-xs text-slate-600">Email</Label><Input data-testid="new-user-email" value={newUser.email || ""} onChange={(e) => setNewUser((u) => ({ ...u, email: e.target.value }))} className="mt-1" /></div>
            <div><Label className="text-xs text-slate-600">Role</Label>
              <Select value={newUser.role || ""} onValueChange={(v) => setNewUser((u) => ({ ...u, role: v }))}>
                <SelectTrigger data-testid="new-user-role" className="mt-1 bg-white"><SelectValue placeholder="Select role" /></SelectTrigger>
                <SelectContent className="bg-white">{roles.filter((r) => r.value !== "amden_super_admin").map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select></div>
            <p className="text-xs text-slate-400">Default password will be the demo password (Amden@2025).</p>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setUOpen(false)}>Cancel</Button><Button className="btn-accent" data-testid="new-user-save" onClick={createUser}>Create user</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent onInteractOutside={(e) => e.preventDefault()} className="bg-white">
          <DialogHeader><DialogTitle className="font-head">Edit user</DialogTitle></DialogHeader>
          {editUser && (
            <div className="space-y-3">
              <div><Label className="text-xs text-slate-600">Name</Label><Input data-testid="edit-user-name" value={editUser.name || ""} onChange={(e) => setEditUser((u) => ({ ...u, name: e.target.value }))} className="mt-1" /></div>
              <div><Label className="text-xs text-slate-600">Email</Label><Input data-testid="edit-user-email" value={editUser.email || ""} onChange={(e) => setEditUser((u) => ({ ...u, email: e.target.value }))} className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-slate-600">Role</Label>
                  <Select value={editUser.role || ""} onValueChange={(v) => setEditUser((u) => ({ ...u, role: v }))}>
                    <SelectTrigger data-testid="edit-user-role" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">{roles.filter((r) => r.value !== "amden_super_admin").map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label className="text-xs text-slate-600">Status</Label>
                  <Select value={editUser.status || "active"} onValueChange={(v) => setEditUser((u) => ({ ...u, status: v }))}>
                    <SelectTrigger data-testid="edit-user-status" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">{["active", "inactive"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-slate-600">Department</Label><Input data-testid="edit-user-department" value={editUser.department || ""} onChange={(e) => setEditUser((u) => ({ ...u, department: e.target.value }))} className="mt-1" /></div>
                <div><Label className="text-xs text-slate-600">Phone</Label><Input data-testid="edit-user-phone" value={editUser.phone || ""} onChange={(e) => setEditUser((u) => ({ ...u, phone: e.target.value }))} className="mt-1" /></div>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setEditUser(null)}>Cancel</Button><Button className="btn-accent" data-testid="edit-user-save" onClick={saveUserEdit}>Save changes</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetUser} onOpenChange={(o) => { if (!o) { setResetUser(null); setResetPw(""); } }}>
        <DialogContent onInteractOutside={(e) => e.preventDefault()} className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="font-head">Reset password{resetUser ? ` — ${resetUser.name}` : ""}</DialogTitle></DialogHeader>
          <div><Label className="text-xs text-slate-600">New password</Label>
            <Input data-testid="reset-password-input" type="text" value={resetPw} onChange={(e) => setResetPw(e.target.value)} placeholder="Min 8 chars, upper + lower + number" className="mt-1" />
            <p className="text-[11px] text-slate-400 mt-1.5">Share this password with the user securely. They can sign in with it immediately.</p>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setResetUser(null)}>Cancel</Button><Button className="btn-accent" data-testid="reset-password-save" onClick={doResetPassword} disabled={!resetPw}>Reset password</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delUser} onOpenChange={(o) => !o && setDelUser(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently delete {delUser?.name}?</AlertDialogTitle>
            <AlertDialogDescription>{delUser?.email} will lose access immediately and the account is gone for good. An audit trail entry will be kept.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-user-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-user-delete" className="bg-red-600 hover:bg-red-700 text-white" onClick={doDeleteUser}>Delete user</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
